/* 🗺️ Battlegrounds — Server-Authoritative Radial Arena (Feb 2026)
 * ---------------------------------------------------------------
 * Renders the 100×100 arena with server-owned positions + shard field.
 *   • Sprites glide between polls (0.6s CSS transition — feels alive)
 *   • Click arena → POST /api/battlegrounds/move (server clamps by dt × speed)
 *   • Click enemy sprite → auto-walk close then fire /battlegrounds/attack
 *   • Click shard OR walk within 6 units → POST /battlegrounds/cargo/pickup-shard-at
 *   • Mini-radar in the corner shows the whole arena at once
 *   • Neutral wildcard is labelled "???" (secret — no reveal text)
 */
import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import BattleFxLayer from "./BattleFx";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ARENA_UNITS = 100;
const NPC_POS = {
  red:  { 1: [18, 40], 2: [14, 55], 3: [10, 45], 4: [ 6, 60] },
  blue: { 1: [82, 40], 2: [86, 55], 3: [90, 45], 4: [94, 60] },
};
const AUTO_PICKUP_RANGE = 6.0;
const DEPOSIT_RANGE = 20.0;   // 🏦 Mar 2026: generous drop-off, matches server 25-unit gate

export default function BattlegroundsMap({
  match, myTeam, viewerUserId, cartsCount, busy, onAttack, onAfterMove, onAfterPickup,
}) {
  const boardRef = useRef(null);
  const [boardSize, setBoardSize] = useState({ w: 0, h: 0 });
  const [pendingMove, setPendingMove] = useState(null);
  const [moving, setMoving] = useState(false);
  const autoPickupLockRef = useRef(new Set());   // shard_ids being processed

  useEffect(() => {
    const measure = () => {
      const el = boardRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setBoardSize({ w: rect.width, h: rect.height });
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  const redHeroes  = match?.red_team?.heroes  || [];
  const blueHeroes = match?.blue_team?.heroes || [];
  const redNpcs    = match?.red_team?.npcs    || [];
  const blueNpcs   = match?.blue_team?.npcs   || [];
  const wildcard   = match?.phantom?.spawned && match?.phantom?.npc?.alive
                       ? match.phantom.npc : null;
  const shards     = match?.shards || [];
  // 🪂 QA Bug #10 (2026-10) — warmup lock. Shards can't be captured
  // until the match goes live. Render a lock badge + swallow clicks.
  const isWarmup   = match?.status === "warmup";

  const meHero = useMemo(() => {
    const all = [...redHeroes, ...blueHeroes];
    return all.find(h => h.user_id === viewerUserId) || null;
  }, [redHeroes, blueHeroes, viewerUserId]);

  // 🏦 My team's Base Sentinel (T4) doubles as the cargo BANK
  const myNpcs = myTeam === "red" ? redNpcs : blueNpcs;
  const myBank = myNpcs.find(n => n.kind === "npc_base_sentinel") || null;
  const bankPos = myBank
    ? [myBank.pos_x ?? NPC_POS[myTeam]?.[4]?.[0] ?? 50,
       myBank.pos_y ?? NPC_POS[myTeam]?.[4]?.[1] ?? 50]
    : null;

  const worldToPx = useCallback((x, y) => ({
    left: (x / ARENA_UNITS) * boardSize.w,
    top:  (y / ARENA_UNITS) * boardSize.h,
  }), [boardSize]);

  const pxToWorld = useCallback((clientX, clientY) => {
    const rect = boardRef.current?.getBoundingClientRect();
    if (!rect) return null;
    const x = ((clientX - rect.left) / rect.width)  * ARENA_UNITS;
    const y = ((clientY - rect.top)  / rect.height) * ARENA_UNITS;
    return [Math.max(0, Math.min(100, x)), Math.max(0, Math.min(100, y))];
  }, []);

  const requestMove = useCallback(async (worldX, worldY) => {
    if (moving) return;
    setMoving(true);
    setPendingMove([worldX, worldY]);
    try {
      const r = await axios.post(`${API}/battlegrounds/move`,
                                   { target_x: worldX, target_y: worldY });
      onAfterMove?.(r.data);
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      if (!/dead|respawn/i.test(detail)) {
        toast.error(detail || "Move failed.");
      }
    } finally {
      setMoving(false);
      setTimeout(() => setPendingMove(null), 400);
    }
  }, [moving, onAfterMove]);

  const requestPickup = useCallback(async (shardId) => {
    if (autoPickupLockRef.current.has(shardId)) return;
    autoPickupLockRef.current.add(shardId);
    try {
      const r = await axios.post(`${API}/battlegrounds/cargo/pickup-shard-at`,
                                   { shard_id: shardId });
      const d = r.data;
      const tierIcon = d.tier === "superior" ? "🟡"
                      : d.tier === "normal"   ? "🟣" : "🟫";
      toast.success(`${tierIcon} Picked up ${d.tier} shard!`);
      onAfterPickup?.(d);
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      // Suppress "too far" noise for auto-pickup attempts
      if (!/Too far|already picked/i.test(detail)) {
        toast.error(detail || "Pickup failed.");
      }
    } finally {
      setTimeout(() => autoPickupLockRef.current.delete(shardId), 800);
    }
  }, [onAfterPickup]);

  const onBoardClick = (e) => {
    const w = pxToWorld(e.clientX, e.clientY);
    if (!w) return;
    requestMove(w[0], w[1]);
  };

  const onEnemyClick = useCallback(async (targetX, targetY, targetId, targetName) => {
    if (busy || moving) return;
    if (meHero) {
      const mx = meHero.pos_x ?? 50, my = meHero.pos_y ?? 50;
      const dx = targetX - mx, dy = targetY - my;
      const dist = Math.sqrt(dx*dx + dy*dy) || 1;
      const stopDist = Math.max(0, dist - 5);
      const nx = mx + (dx / dist) * stopDist;
      const ny = my + (dy / dist) * stopDist;
      await requestMove(nx, ny);
    }
    onAttack?.(targetId, targetName);
  }, [busy, moving, meHero, requestMove, onAttack]);

  // ── Auto-pickup: whenever I'm within range of a shard, grab it silently ──
  useEffect(() => {
    if (isWarmup) return;                             // 🪂 locked during warmup
    if (!meHero || meHero.alive === false) return;
    const mx = meHero.pos_x, my = meHero.pos_y;
    if (mx == null || my == null) return;
    for (const s of shards) {
      if (s.picked_up || autoPickupLockRef.current.has(s.shard_id)) continue;
      const dx = (s.pos_x || 0) - mx;
      const dy = (s.pos_y || 0) - my;
      if (Math.sqrt(dx*dx + dy*dy) <= AUTO_PICKUP_RANGE) {
        requestPickup(s.shard_id);
        break;    // one at a time
      }
    }
  }, [meHero, shards, requestPickup]);

  // ── Auto-deposit: standing at my Base Sentinel bank with carts → dump ──
  const depositLockRef = useRef(false);
  const requestDeposit = useCallback(async () => {
    if (depositLockRef.current) return;
    depositLockRef.current = true;
    try {
      const r = await axios.post(`${API}/battlegrounds/cargo/deposit`);
      const d = r.data;
      toast.success(
        `🏦 Banked ${d.ambergris_deposited}A → +${d.chests_earned} chest${d.chests_earned === 1 ? "" : "s"}!` +
        (d.carryover_after > 0 ? ` (${d.carryover_after}A carries over)` : "")
      );
      onAfterPickup?.(d);
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      if (!/Too far|No cargo/i.test(detail)) {
        toast.error(detail || "Deposit failed.");
      }
    } finally {
      setTimeout(() => { depositLockRef.current = false; }, 2500);
    }
  }, [onAfterPickup]);

  useEffect(() => {
    if (!meHero || meHero.alive === false || cartsCount <= 0 || !bankPos) return;
    const mx = meHero.pos_x, my = meHero.pos_y;
    if (mx == null || my == null) return;
    const dx = bankPos[0] - mx, dy = bankPos[1] - my;
    if (Math.sqrt(dx*dx + dy*dy) <= DEPOSIT_RANGE) {
      requestDeposit();
    }
  }, [meHero, cartsCount, bankPos, requestDeposit]);

  if (!match) return null;
  const oppTeam = myTeam === "red" ? "blue" : "red";

  return (
    <div className="relative w-full rounded-2xl border-2 border-amber-700/50 bg-black/70 overflow-hidden"
         data-testid="bg-arena-map">
      {/* Header strip */}
      <div className="absolute top-0 left-0 right-0 z-30 px-3 py-2 flex items-center justify-between text-[10px] uppercase tracking-widest bg-gradient-to-b from-black/80 to-transparent">
        <span className="text-red-300 font-black">🔴 RED · {redHeroes.length}</span>
        <span className="text-neutral-400">🗺️ Click to move · click enemies to duel · 🪨 pick up · dump 📦 at your 🏦 Sentinel</span>
        <span className="text-blue-300 font-black">BLUE 🔵 · {blueHeroes.length}</span>
      </div>

      {/* The board */}
      <div ref={boardRef}
           onClick={onBoardClick}
           className="relative w-full cursor-crosshair select-none"
           style={{
             aspectRatio: "2 / 1",
             backgroundColor: "#08080e",
             backgroundImage:
               "radial-gradient(circle at 10% 50%, rgba(239,68,68,0.20), transparent 35%)," +
               "radial-gradient(circle at 90% 50%, rgba(59,130,246,0.20), transparent 35%)," +
               "radial-gradient(circle at 50% 50%, rgba(245,158,11,0.10), transparent 22%)," +
               "repeating-linear-gradient(45deg, rgba(255,255,255,0.015) 0 2px, transparent 2px 6px)",
           }}>

        {/* Neutral centre ring outline */}
        <div className="absolute rounded-full border border-amber-700/30 pointer-events-none"
             style={{
               left: "35%", top: "20%", width: "30%", height: "60%",
               background: "radial-gradient(circle, rgba(245,158,11,0.05), transparent)",
             }} />
        <div className="absolute top-6 bottom-6 left-1/2 w-px bg-neutral-800/70 pointer-events-none" />

        {/* 🏦 My team's bank zone (own Base Sentinel = deposit point) */}
        {bankPos && (
          <BankZone
            worldPos={bankPos}
            worldToPx={worldToPx}
            hasCargo={cartsCount > 0}
            onClick={(e) => {
              e.stopPropagation();
              requestMove(bankPos[0], bankPos[1]);
            }}
          />
        )}

        {/* Shards from server state */}
        {shards.map(s => (
          <Shard key={s.shard_id}
                 shard={s}
                 worldToPx={worldToPx}
                 locked={isWarmup}
                 onClick={(e) => {
                   if (s.picked_up) return;
                   e.stopPropagation();
                   if (isWarmup) {
                     // 🪂 QA Bug #10 — clear UX feedback during warmup
                     toast.message("Shards can't be captured during warmup — grab one once the match starts.");
                     return;
                   }
                   requestPickup(s.shard_id);
                 }} />
        ))}

        {/* Neutral wildcard — labelled "???"  */}
        {wildcard && (
          <Sprite
            worldPos={[wildcard.pos_x ?? 50, wildcard.pos_y ?? 50]}
            worldToPx={worldToPx}
            icon="❔"
            label="???"
            ring="ring-2 ring-neutral-400/60 shadow-[0_0_18px_rgba(180,180,180,0.35)]"
            bg="rgba(120,120,140,0.35)"
            title="???"
            testid="sprite-neutral-wildcard"
            onClick={(e) => {
              e.stopPropagation();
              onEnemyClick(wildcard.pos_x ?? 50, wildcard.pos_y ?? 50,
                           wildcard.user_id || "npc_phantom", "???");
            }}
          />
        )}

        {/* Ally + enemy defense NPCs */}
        {redNpcs.filter(n => n.breach_order).map(n => (
          <NpcSprite key={n.user_id} n={n} side="red" myTeam={myTeam}
                     worldToPx={worldToPx}
                     onClick={(e) => {
                       e.stopPropagation();
                       if (n.alive && myTeam !== "red") {
                         onEnemyClick(n.pos_x ?? NPC_POS.red[n.breach_order][0],
                                      n.pos_y ?? NPC_POS.red[n.breach_order][1],
                                      n.user_id, n.name);
                       } else if (myTeam === "red" && n.kind === "npc_base_sentinel") {
                         requestMove(n.pos_x ?? NPC_POS.red[4][0],
                                     n.pos_y ?? NPC_POS.red[4][1]);
                       }
                     }} />
        ))}
        {blueNpcs.filter(n => n.breach_order).map(n => (
          <NpcSprite key={n.user_id} n={n} side="blue" myTeam={myTeam}
                     worldToPx={worldToPx}
                     onClick={(e) => {
                       e.stopPropagation();
                       if (n.alive && myTeam !== "blue") {
                         onEnemyClick(n.pos_x ?? NPC_POS.blue[n.breach_order][0],
                                      n.pos_y ?? NPC_POS.blue[n.breach_order][1],
                                      n.user_id, n.name);
                       }
                     }} />
        ))}

        {/* All 20 heroes — both sides visible */}
        {[...redHeroes, ...blueHeroes].map((h) => {
          const isMe = h.user_id === viewerUserId;
          const side = h.team || (redHeroes.includes(h) ? "red" : "blue");
          const isEnemy = side === oppTeam;
          return (
            <HeroSprite
              key={h.user_id}
              hero={h}
              side={side}
              isMe={isMe}
              isEnemy={isEnemy}
              worldToPx={worldToPx}
              cartsCount={isMe ? cartsCount : 0}
              onClick={(e) => {
                if (!isEnemy || h.alive === false) return;
                e.stopPropagation();
                onEnemyClick(h.pos_x ?? 50, h.pos_y ?? 50, h.user_id, h.name);
              }}
            />
          );
        })}

        {/* Optimistic move ping */}
        {pendingMove && (
          <div className="absolute rounded-full border-2 border-amber-300/80 pointer-events-none animate-ping"
               style={{
                 left: worldToPx(pendingMove[0], pendingMove[1]).left - 10,
                 top:  worldToPx(pendingMove[0], pendingMove[1]).top  - 10,
                 width: 20, height: 20,
               }} />
        )}

        {/* Mini-radar */}
        <MiniRadar
          redHeroes={redHeroes}
          blueHeroes={blueHeroes}
          shards={shards}
          me={meHero}
          myTeam={myTeam}
        />

        {/* 💥 Battle FX overlay — damage floats, crit shake, streak banners */}
        <BattleFxLayer />
      </div>

      <div className="px-3 py-2 flex items-center justify-between text-[10px] text-neutral-500 bg-black/60 border-t border-neutral-900">
        <span>🔴 Red base</span>
        <span>🪨 Walk on shards to collect · 🏦 walk to YOUR T4 Sentinel to bank cargo (60A = 1 chest)</span>
        <span>🔵 Blue base</span>
      </div>
    </div>
  );
}

// ─────────────────────────────── sub-sprites ───────────────────────────────
function BankZone({ worldPos, worldToPx, hasCargo, onClick }) {
  const c = worldToPx(worldPos[0], worldPos[1]);
  const edge = worldToPx(worldPos[0] + DEPOSIT_RANGE, worldPos[1]);
  const r = Math.max(20, Math.abs(edge.left - c.left));
  return (
    <button
      type="button"
      data-testid="bg-bank-zone"
      title={hasCargo ? "🏦 BANK — walk here to deposit your cargo!" : "🏦 Your team's bank (Base Sentinel)"}
      onClick={onClick}
      className={`absolute rounded-full border-2 border-dashed cursor-pointer
                  ${hasCargo ? "border-amber-300/90 animate-pulse" : "border-amber-600/40"}`}
      style={{
        left: c.left - r, top: c.top - r, width: r * 2, height: r * 2,
        background: hasCargo
          ? "radial-gradient(circle, rgba(245,158,11,0.20), transparent 70%)"
          : "radial-gradient(circle, rgba(245,158,11,0.07), transparent 70%)",
        padding: 0,
        transition: "left 0.6s cubic-bezier(0.4,0,0.2,1), top 0.6s cubic-bezier(0.4,0,0.2,1)",
      }}>
      <span className="absolute -top-4 left-1/2 -translate-x-1/2 text-[9px] font-black tracking-widest text-amber-300 whitespace-nowrap drop-shadow-[0_0_4px_rgba(0,0,0,0.9)]">
        🏦 BANK{hasCargo ? " — DUMP HERE!" : ""}
      </span>
    </button>
  );
}

function Sprite({ worldPos, worldToPx, icon, label, ring = "", bg = "rgba(0,0,0,0.4)",
                  onClick, title, testid, size = 34, subLabel }) {
  const p = worldToPx(worldPos[0], worldPos[1]);
  return (
    <button
      type="button"
      title={title || label}
      data-testid={testid}
      onClick={onClick}
      className={`absolute flex flex-col items-center justify-center will-change-transform
                  ${ring} ${onClick ? "cursor-pointer hover:scale-110 active:scale-95" : "pointer-events-none"}`}
      style={{
        left: p.left - size / 2,
        top:  p.top  - size / 2,
        width: size,
        height: size,
        backgroundColor: bg,
        borderRadius: 8,
        padding: 0,
        border: "none",
        transition: "left 0.6s cubic-bezier(0.4,0,0.2,1), top 0.6s cubic-bezier(0.4,0,0.2,1), transform 0.15s ease-out",
      }}>
      <span className="text-xl leading-none">{icon}</span>
      {label && <span className="text-[8px] font-black text-white/90 leading-none mt-0.5 truncate max-w-full px-0.5">{label}</span>}
      {subLabel && <span className="text-[7px] text-white/60 leading-none">{subLabel}</span>}
    </button>
  );
}

function Shard({ shard, worldToPx, onClick, locked = false }) {
  if (shard.picked_up) return null;
  const p = worldToPx(shard.pos_x, shard.pos_y);
  const tier = shard.tier;
  const sz = tier === "superior" ? 20 : tier === "normal" ? 16 : 12;
  const color = tier === "superior" ? "from-amber-200 to-amber-500"
              : tier === "normal"   ? "from-violet-200 to-violet-500"
                                    : "from-yellow-600 to-yellow-800";
  return (
    <button
      type="button"
      onClick={onClick}
      title={locked
        ? "Locked during warmup — shards unlock when the match goes live."
        : `${tier} shard — walk on it or click`}
      data-testid={`shard-${shard.shard_id}`}
      data-locked={locked ? "true" : "false"}
      className={`absolute transition-transform ${locked ? "cursor-not-allowed opacity-50 grayscale" : "cursor-pointer hover:scale-125 active:scale-95"}`}
      style={{
        left: p.left - sz/2, top: p.top - sz/2, width: sz, height: sz,
        padding: 0, border: "none", background: "transparent",
      }}>
      <div className={`w-full h-full rounded-full bg-gradient-to-br ${color} ${locked ? "" : "animate-pulse"}
                       shadow-[0_0_14px_rgba(245,158,11,0.7)] ring-1 ring-white/40`} />
      {locked && (
        <div
          data-testid={`shard-${shard.shard_id}-lock`}
          className="absolute -top-2 -right-2 text-[9px] leading-none bg-black/80 text-amber-200 rounded-full px-1 py-0.5 border border-amber-500/60 shadow"
          style={{ pointerEvents: "none" }}
        >
          🔒
        </div>
      )}
    </button>
  );
}

function NpcSprite({ n, side, myTeam, worldToPx, onClick }) {
  const fallback = NPC_POS[side]?.[n.breach_order] || [50, 50];
  const worldPos = [n.pos_x ?? fallback[0], n.pos_y ?? fallback[1]];
  const dead = !n.alive;
  const isEnemy = side !== myTeam;
  const ring = dead
    ? "opacity-30 grayscale"
    : isEnemy
      ? "ring-2 ring-red-500/70 hover:ring-red-300 shadow-[0_0_10px_rgba(239,68,68,0.35)]"
      : "ring-2 ring-blue-500/70 shadow-[0_0_10px_rgba(59,130,246,0.35)]";
  return (
    <Sprite
      worldPos={worldPos}
      worldToPx={worldToPx}
      icon={n.icon || "🛡️"}
      label={`T${n.tier || "?"}`}
      subLabel={dead ? "DOWN" : (n.hp != null ? `${n.hp}HP` : null)}
      bg={side === "red" ? "rgba(127,29,29,0.35)" : "rgba(29,78,127,0.35)"}
      ring={ring}
      title={`${n.name} · ${n.hp ?? n.max_hp}HP · +${n.honor_reward}🏅`}
      testid={`sprite-npc-${side}-${n.kind}`}
      onClick={dead ? undefined : onClick}
      size={36}
    />
  );
}

function HeroSprite({ hero, side, isMe, isEnemy, worldToPx, cartsCount = 0, onClick }) {
  const worldPos = [hero.pos_x ?? (side === "red" ? 10 : 90), hero.pos_y ?? 50];
  const dead = hero.alive === false;
  const icon = hero.icon || (hero.kind === "npc_soldier" ? "🤖" : (side === "red" ? "⚔️" : "🛡️"));
  const ring = dead
    ? "opacity-30 grayscale"
    : isMe
      ? "ring-2 ring-amber-300 shadow-[0_0_16px_rgba(245,158,11,0.75)] z-20"
      : isEnemy
        ? "ring-1 ring-red-500/70 hover:ring-red-300 z-10"
        : "ring-1 ring-blue-500/70 z-10";
  const bg = isMe
    ? "rgba(245,158,11,0.55)"
    : side === "red"
      ? "rgba(239,68,68,0.30)"
      : "rgba(59,130,246,0.30)";
  const nameShort = (hero.name || (hero.kind === "npc_soldier" ? "Soldier" : "Hero")).slice(0, 7);
  const subLabel = dead ? "DOWN" : (hero.poker_face ? "??HP"
                    : (isMe && hero.hp != null ? `${hero.hp}HP` : null));
  return (
    <>
      <Sprite
        worldPos={worldPos}
        worldToPx={worldToPx}
        icon={icon}
        label={nameShort}
        subLabel={subLabel}
        bg={bg}
        ring={ring}
        title={`${hero.name}${isMe ? " (You)" : ""}${hero.kind === "npc_soldier" ? " · AI" : ""}`}
        testid={isMe ? "sprite-me" : `sprite-hero-${hero.user_id}`}
        onClick={isEnemy && !dead ? onClick : undefined}
        size={isMe ? 40 : 34}
      />
      {isMe && cartsCount > 0 && (
        <CargoTrail worldPos={worldPos} side={side} count={cartsCount} worldToPx={worldToPx} />
      )}
    </>
  );
}

function CargoTrail({ worldPos, side, count, worldToPx }) {
  const dir = side === "red" ? -1 : 1;
  const shown = Math.min(4, count);
  return (
    <>
      {Array.from({ length: shown }).map((_, i) => {
        const trailWorld = [Math.max(0, Math.min(100, worldPos[0] + dir * (i + 1) * 2)), worldPos[1]];
        const p = worldToPx(trailWorld[0], trailWorld[1]);
        return (
          <div key={i}
               className="absolute pointer-events-none"
               style={{
                 left: p.left - 10,
                 top:  p.top  - 10,
                 width: 20, height: 20,
                 opacity: 0.55 - i * 0.10,
                 fontSize: 16, lineHeight: "20px", textAlign: "center",
                 transition: "left 0.6s cubic-bezier(0.4,0,0.2,1), top 0.6s cubic-bezier(0.4,0,0.2,1)",
               }}>
            📦
          </div>
        );
      })}
    </>
  );
}

// ─── Mini-radar (top-right corner) ────────────────────────────────
function MiniRadar({ redHeroes, blueHeroes, shards, me, myTeam }) {
  const RADAR_W = 140, RADAR_H = 70;
  const dot = (x, y, color, size = 3) => (
    <div key={`${color}-${x}-${y}-${Math.random()}`}
         className="absolute rounded-full"
         style={{
           left: (x / 100) * RADAR_W - size/2,
           top:  (y / 100) * RADAR_H - size/2,
           width: size, height: size,
           background: color,
           transition: "left 0.6s, top 0.6s",
         }} />
  );
  return (
    <div className="absolute top-10 right-2 z-20 rounded-lg border border-amber-700/50 bg-black/70 p-1.5 shadow-[0_0_10px_rgba(0,0,0,0.6)]"
         data-testid="bg-mini-radar">
      <div className="text-[8px] uppercase tracking-widest text-amber-300/60 mb-1 text-center">RADAR</div>
      <div className="relative" style={{ width: RADAR_W, height: RADAR_H, background: "rgba(20,20,30,0.6)" }}>
        {/* Base tints */}
        <div className="absolute inset-y-0 left-0 w-1/5 bg-red-900/30" />
        <div className="absolute inset-y-0 right-0 w-1/5 bg-blue-900/30" />
        {/* Shards */}
        {shards.filter(s => !s.picked_up).map(s => dot(s.pos_x, s.pos_y, "#f59e0b", 3))}
        {/* Red team */}
        {redHeroes.map(h => h.alive === false ? null :
          dot(h.pos_x || 10, h.pos_y || 50, myTeam === "red" ? "#3b82f6" : "#ef4444", 3))}
        {/* Blue team */}
        {blueHeroes.map(h => h.alive === false ? null :
          dot(h.pos_x || 90, h.pos_y || 50, myTeam === "blue" ? "#3b82f6" : "#ef4444", 3))}
        {/* Me — bigger amber dot */}
        {me && me.alive !== false &&
          dot(me.pos_x || 50, me.pos_y || 50, "#fbbf24", 6)}
      </div>
    </div>
  );
}
